import { useState, useCallback, useMemo, useEffect } from 'react';
import { debounce } from 'lodash';

const DEBOUNCE_DELAY = 500;
const ROWS_PER_PAGE_STORAGE_KEY = 'transportCloud_rowsPerPage';

const loadRowsPerPageFromStorage = (): number | null => {
  try {
    const raw = localStorage.getItem(ROWS_PER_PAGE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
};

const saveRowsPerPageToStorage = (rowsPerPage: number): void => {
  try {
    localStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, String(rowsPerPage));
  } catch {}
};

export const useTableState = (
  initialParams: any,
  persistedFilters?: {
    searchValue: string;
    checkedOnlyMine: boolean | null;
    loadingDeliveryDateFrom: string;
    loadingDeliveryDateTo: string;
  },
  defaultOnlyMine: boolean = true,
) => {
  const [searchValue, setSearchValue] = useState(
    persistedFilters?.searchValue ?? initialParams.filterText ?? '',
  );
  const [checkedOnlyMine, setCheckedOnlyMine] = useState(
    persistedFilters?.checkedOnlyMine ?? defaultOnlyMine,
  );
  const [page, setPage] = useState((initialParams.pageNumber || 1) - 1);
  const [rowsPerPage, setRowsPerPage] = useState(
    loadRowsPerPageFromStorage() ?? initialParams.resultSize ?? 10,
  );

  useEffect(() => {
    saveRowsPerPageToStorage(rowsPerPage);
  }, [rowsPerPage]);
  const [loadingDeliveryDateFrom, setLoadingDeliveryDateFrom] = useState(
    persistedFilters?.loadingDeliveryDateFrom ?? '',
  );
  const [loadingDeliveryDateTo, setLoadingDeliveryDateTo] = useState(
    persistedFilters?.loadingDeliveryDateTo ?? '',
  );

  const debouncedSearch = useMemo(
    () => debounce(() => setPage(0), DEBOUNCE_DELAY),
    [],
  );

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
      debouncedSearch();
    },
    [debouncedSearch],
  );

  const handleOnlyMineCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPage(0);
      setCheckedOnlyMine(e.target.checked);
    },
    [],
  );

  const handleDateFromChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPage(0);
      setLoadingDeliveryDateFrom(event.target.value);
    },
    [],
  );

  const handleDateToChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPage(0);
      setLoadingDeliveryDateTo(event.target.value);
    },
    [],
  );

  const handleChangePage = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
      newPage: number,
    ) => setPage(newPage),
    [],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  return {
    searchValue,
    checkedOnlyMine,
    page,
    rowsPerPage,
    loadingDeliveryDateFrom,
    loadingDeliveryDateTo,
    setPage,
    setLoadingDeliveryDateFrom,
    setLoadingDeliveryDateTo,
    handleSearch,
    handleOnlyMineCheckbox,
    handleDateFromChange,
    handleDateToChange,
    handleChangePage,
    handleChangeRowsPerPage,
    debouncedSearch,
  };
};
